import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCamera,
  FiUpload,
  FiX,
  FiSave,
  FiCheckCircle,
  FiImage,
} from 'react-icons/fi';
import {
  getGraderOrderById,
  saveGradingDraft,
  completeGrading,
} from '../services/api';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { formatAddress } from '../services/wallet';
import LoadingSpinner from '../components/LoadingSpinner';
import { useBeginWalletConnect } from '../contexts/WalletConnectReminderContext';

/** Sub-grade fields must use `.` as decimal separator (API / Number). Commas become `.`; other chars stripped. */
function normalizeSubGradeDecimalInput(raw) {
  if (raw === '' || raw == null) return '';
  let s = String(raw).replace(/,/g, '.');
  s = s.replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) {
    s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
  }
  return s;
}

function gradeFieldFromApi(v) {
  if (v == null || v === '') return '';
  return normalizeSubGradeDecimalInput(String(v));
}

export default function GraderInspection() {
  const beginWalletConnect = useBeginWalletConnect();
  const { id } = useParams();
  const navigate = useNavigate();
  const { address } = useAccount();
  const wallet = address ?? null;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [photos, setPhotos] = useState([]);

  const [form, setForm] = useState({
    gradeCentering: '',
    gradeCorners: '',
    gradeEdges: '',
    gradeSurface: '',
    gradingNotes: '',
    conditionReport: '',
    certificate: '',
  });

  const loadOrder = async (w) => {
    const res = await getGraderOrderById(w, id);
    const o = res.data?.data || null;
    setOrder(o);

    const existingPhotos = Array.isArray(o?.inspectionPhotos) ? o.inspectionPhotos : [];
    setPhotos(existingPhotos);

    setForm({
      gradeCentering: gradeFieldFromApi(o?.gradeCentering),
      gradeCorners: gradeFieldFromApi(o?.gradeCorners),
      gradeEdges: gradeFieldFromApi(o?.gradeEdges),
      gradeSurface: gradeFieldFromApi(o?.gradeSurface),
      gradingNotes: o?.gradingNotes ?? '',
      conditionReport: o?.conditionReport ?? '',
      certificate: o?.certificate ?? '',
    });
  };

  useEffect(() => {
    if (!wallet) {
      setLoading(false);
      return;
    }
    setLoading(true);
    loadOrder(wallet)
      .catch((e) => console.error('Failed to load grader order:', e))
      .finally(() => setLoading(false));
  }, [wallet, id]);

  const overall = useMemo(() => {
    const a = Number(normalizeSubGradeDecimalInput(form.gradeCentering));
    const b = Number(normalizeSubGradeDecimalInput(form.gradeCorners));
    const c = Number(normalizeSubGradeDecimalInput(form.gradeEdges));
    const d = Number(normalizeSubGradeDecimalInput(form.gradeSurface));
    if (![a, b, c, d].every((x) => Number.isFinite(x) && x > 0)) return null;
    return ((a + b + c + d) / 4).toFixed(1);
  }, [form]);

  const apiRoot = (import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1').replace(/\/api\/v1$/, '');
  const uploadEndpoint = `${apiRoot}/api/v1/upload/gcs/grading/${id}/photos`;

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    for (const f of files) fd.append('photos', f);

    setUploading(true);
    try {
      const r = await fetch(uploadEndpoint, {
        method: 'POST',
        headers: {
          'X-Wallet-Address': wallet,
        },
        body: fd,
      });
      const j = await r.json();
      if (!r.ok || !j?.success) {
        throw new Error(j?.error || j?.message || 'Upload failed');
      }
      // Store full photo objects (url, gsUrl) - images go to Pinata at mint time
      const newPhotos = (j?.data?.photos || []).filter((p) => p?.url).map((p) => ({
        url: p.url,
        gsUrl: p.gsUrl || null,
      }));
      setPhotos((prev) => {
        const next = [...prev, ...newPhotos];
        if (next.length > 5) {
          alert('Maximum 5 inspection photos allowed per order. Only the first 5 will be saved.');
          return next.slice(0, 5);
        }
        return next;
      });
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) => {
    const ok = window.confirm('Remove this photo?');
    if (!ok) return;
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await saveGradingDraft(wallet, id, {
        ...form,
        inspectionPhotos: photos,
      });
      alert('Draft saved.');
      await loadOrder(wallet);
    } catch (e) {
      console.error(e);
      alert('Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!overall) {
      alert('Please complete all sub-grades.');
      return;
    }
    if (photos.length === 0) {
      alert('Please upload at least one inspection photo.');
      return;
    }
    const ok = window.confirm('Submit final grading? This action cannot be undone.');
    if (!ok) return;

    setSaving(true);
    try {
      await completeGrading(wallet, id, {
        ...form,
        inspectionPhotos: photos,
      });
      alert('Grading submitted.');
      navigate('/grader');
    } catch (e) {
      console.error(e);
      alert('Failed to submit grading.');
    } finally {
      setSaving(false);
    }
  };

  if (!wallet) {
    return (
      <div className="page-container">
        <div className="max-w-md mx-auto text-center py-20">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{
              background: 'rgba(180, 156, 245, 0.1)',
              border: '1px solid rgba(180, 156, 245, 0.2)',
            }}
          >
            <FiCheckCircle size={32} className="text-cyan-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white">Inspection & Grading</h1>
          <p className="text-gray-400 mb-8">Connect your wallet to continue.</p>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={() => beginWalletConnect(openConnectModal)} className="btn-primary">
                Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" text="Loading order..." />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-container">
        <div className="card text-center py-12">
          <p className="text-red-400">Order not found</p>
          <button onClick={() => navigate('/grader')} className="btn-primary mt-4">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button
        onClick={() => navigate('/grader')}
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <FiArrowLeft size={20} />
        Back to Dashboard
      </button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">Inspection & Grading</h1>
        <p className="text-gray-400">
          Connected as <span className="font-mono text-cyan-400">{formatAddress(wallet)}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Photos */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <FiImage className="text-cyan-400" />
              Original Submission
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-400 mb-2">Front</div>
                <img
                  src={order.card?.frontImage || '/placeholder-card.png'}
                  alt="Front"
                  className="w-full rounded-lg border border-white/10"
                />
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-2">Back</div>
                <img
                  src={order.card?.backImage || '/placeholder-card.png'}
                  alt="Back"
                  className="w-full rounded-lg border border-white/10"
                />
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <FiCamera className="text-cyan-400" />
              Inspection Photos ({photos.length}/5)
            </h3>
            <p className="text-gray-400 text-xs mb-4">Maximum 5 inspection photos per order. Images are stored until mint.</p>

            <label className={`block w-full ${photos.length >= 5 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
              <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center hover:border-cyan-500/60 transition">
                <FiUpload className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">
                  {uploading ? 'Uploading...' : photos.length >= 5 ? 'Maximum 5 photos reached' : 'Click to upload photos'}
                </p>
                <p className="text-gray-600 text-xs mt-1">Up to 5 photos per order</p>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => uploadPhotos(Array.from(e.target.files || []))}
                disabled={uploading || photos.length >= 5}
                className="hidden"
              />
            </label>

            {photos.length > 0 && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                {photos.map((p, index) => {
                  const url = typeof p === 'object' && p ? p.url : typeof p === 'string' ? p : null;
                  if (!url) return null;
                  return (
                  <div key={url + index} className="relative group">
                    <img
                      src={url}
                      alt={`Inspection ${index + 1}`}
                      className="w-full h-40 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                    >
                      <FiX />
                    </button>
                  </div>
                );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Form */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <FiCheckCircle className="text-cyan-400" />
              Grade Components
            </h3>

            <div className="space-y-4">
              <GradeInput
                label="Centering"
                value={form.gradeCentering}
                onChange={(v) => setForm((p) => ({ ...p, gradeCentering: v }))}
              />
              <GradeInput
                label="Corners"
                value={form.gradeCorners}
                onChange={(v) => setForm((p) => ({ ...p, gradeCorners: v }))}
              />
              <GradeInput
                label="Edges"
                value={form.gradeEdges}
                onChange={(v) => setForm((p) => ({ ...p, gradeEdges: v }))}
              />
              <GradeInput
                label="Surface"
                value={form.gradeSurface}
                onChange={(v) => setForm((p) => ({ ...p, gradeSurface: v }))}
              />
            </div>

            {overall && (
              <div className="mt-6 p-4 rounded-lg border border-cyan-500/30 bg-cyan-500/10">
                <div className="text-gray-400 text-sm mb-1">Overall Grade</div>
                <div className="text-cyan-400 font-bold text-4xl">{overall}</div>
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="text-white font-bold mb-4">Detailed Report</h3>
            <div className="space-y-4">
              <TextArea
                label="Condition Report"
                value={form.conditionReport}
                onChange={(v) => setForm((p) => ({ ...p, conditionReport: v }))}
              />
              <TextArea
                label="Grading Notes"
                value={form.gradingNotes}
                onChange={(v) => setForm((p) => ({ ...p, gradingNotes: v }))}
              />
              <TextArea
                label="Certificate of Authenticity"
                value={form.certificate}
                onChange={(v) => setForm((p) => ({ ...p, certificate: v }))}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saving}
              className="flex-1 px-6 py-3 bg-gray-700 text-white font-bold rounded-lg hover:bg-gray-600 disabled:opacity-50 transition flex items-center justify-center gap-2"
            >
              <FiSave />
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              type="button"
              onClick={handleComplete}
              disabled={saving || !overall || photos.length === 0}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-lg hover:opacity-90 disabled:opacity-50 transition flex items-center justify-center gap-2"
            >
              <FiCheckCircle />
              {saving ? 'Submitting...' : 'Submit Final Grade'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GradeInput({ label, value, onChange }) {
  const display = value === null || value === undefined ? '' : String(value);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={display}
        onChange={(e) => onChange(normalizeSubGradeDecimalInput(e.target.value))}
        placeholder="e.g. 9.5 (use . not ,)"
        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
      />
      <p className="text-gray-500 text-xs mt-1">1.0 - 10.0 — use point (.) as decimal separator</p>
    </div>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white resize-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
      />
    </div>
  );
}

